// TOTALLY NORMAL TOWN — the simulation. Deterministic, fixed step (SIM_HZ), no rendering, no physics engine: bodies are
// circles on the ground pushed out of each other and out of the square's static colliders (shared/map.ts).
//
// It owns everything that decides the game: the townsfolk (NPC routines live in npc.ts), the Sneaks' verbs, the
// Inspector's Popper and whistle, the Crier's calls, capsules and the vault, the bus, the parade float, and the round
// (intro → play → results, role rotation, onboarding rounds). The server runs it; the browser's offline driver runs the
// very same code; the headless gates run it hundreds of times. Inputs come in through Seat.input (+ pop / whistle /
// mark), the same way for humans and bots (shared/bots.ts).
//
// What may leave the sim is decided in two functions at the bottom: publicView() (what everyone may know) and
// privateView(seat) (what one seat may know about itself). The wire (protocol.ts) and the bots read ONLY those.
import * as T from "./tuning";
import { CALLS, type Call } from "./tuning";
import { makeRng, range, int, pick, weighted, shuffle, type Rng } from "./rng";
import * as M from "./map";
import { RESIDENTS, INSPECTOR_COSTUME, TOWN_NAMES } from "./residents";
import { isParade } from "./parade";
import { newBrain, npcThink, npcRelease, npcPlan, npcResumeAfterCall, type NpcBrain } from "./npc";

export const DT = 1 / T.SIM_HZ;

/** animation states = RIG.md clip names + 'freeze' (mixer timeScale 0 on the current pose) + 'ragdoll' (verlet) */
export const ANIMS = ["idle", "walk", "run", "browse", "chat", "sit_bench", "shoelace", "look_around", "dance", "sit_ground", "wave", "jump", "clap", "angry", "getup", "cheer", "point", "freeze", "ragdoll"] as const;
export type Anim = typeof ANIMS[number];
export const CALL_ANIM: Record<Call, Anim> = { dance: "dance", freeze: "freeze", sit: "sit_ground", wave: "wave", jump: "jump", clap: "clap" };

export type Kind = "npc" | "sneak" | "inspector";
export type Role = "sneak" | "inspector";
export type Phase = "intro" | "play" | "results";
/** free: routine / player control · call: performing a call (locked) · tumble: popped innocent (ragdoll → getup → angry)
 *  panic: running from a pop · popped: a caught Sneak's reveal ragdoll (then gone) */
export type Mode = "free" | "call" | "tumble" | "panic" | "popped";
export type Rarity = typeof T.RARITY[number]["id"];

export type Cosmetics = { mask: number; colour: number; fx: number; hat: number; ray: number };
export const NO_COSMETICS: Cosmetics = { mask: 0, colour: 0, fx: 0, hat: 0, ray: 0 };
export type SeatInput = { mx: number; mz: number; sprint: boolean; act: boolean };
export type Vec3 = { x: number; y: number; z: number };

export interface Body {
  uid: number;              // internal, stable for the body's life (incl. bus trips); never on the wire
  id: number;               // the wire id: random, re-issued on every bus arrival and every round, never ordered by kind
  kind: Kind; seat: number; costume: number;
  x: number; z: number; h: number;          // h = yaw: facing (sin h, cos h)
  vx: number; vz: number;
  anim: Anim; animAt: number;               // tick the anim started
  mode: Mode; modeAt: number; modeUntil: number;
  anchored: boolean;                        // sitting on a bench: never pushed, skips static collision
  blunderUntil: number; glintUntil: number; panicUntil: number;
  revealed: boolean; removeAt: number;
  visible: boolean;
  face: number | null;                      // heading to turn to while standing (POI facing, a stall, a target)
  brain: NpcBrain | null;
  hist: Float32Array; histN: number;        // recent positions (x,z pairs) for the pop rewind
  callAnim: Anim | null;                    // the call being performed (mode 'call')
  freezeCall: boolean;                      // a Sneak's FREEZE answer: any move input during it is a blunder
}

export type SeatStats = {
  stashes: Rarity[]; golden: boolean; perfects: number; answered: number; blunders: number; popped: number;
  catches: number; misses: number; whiffs: number; whistlesHeard: number; whistlesIgnored: number; whistlesFooled: number; grabs: number;
};
const zeroStats = (): SeatStats => ({ stashes: [], golden: false, perfects: 0, answered: 0, blunders: 0, popped: 0, catches: 0, misses: 0, whiffs: 0, whistlesHeard: 0, whistlesIgnored: 0, whistlesFooled: 0, grabs: 0 });

export type SeatAction = { t: "pop"; id: number; aim: Vec3 | null } | { t: "whistle"; call?: Call } | { t: "mark"; id: number; aim: Vec3 };

export interface Seat {
  i: number; human: boolean; name: string; pid: string; cos: Cosmetics;
  newcomer: boolean;                        // first visit: their first round here is an onboarding round
  role: Role; body: Body | null;
  input: SeatInput; prevAct: boolean; pending: SeatAction[];
  respawnAt: number;                        // a caught Sneak steps off the bus at/after this (-1 = not waiting)
  carry: 0 | 1 | 2;                         // 1 = a capsule, 2 = the Golden Capsule
  grabT: number; stashT: number; nextGlint: number; lookT: number;
  callId: number; callDone: boolean; answerAt: number; answerPerfect: boolean;
  whistleId: number;
  charges: number; cooldownUntil: number; regenAt: number; whistleReadyAt: number; mark: number;
  roundsPlayed: number; sinceInsp: number; inRound: boolean;
  stats: SeatStats;
}

/** events: `to` set = private to that seat; unset = public. The server routes them; bots read the same split. */
export type SimEvent =
  | { k: "round_start"; n: number; parade: boolean; onboarding: boolean; inspector: number; inspectorSeat: number }
  | { k: "phase"; phase: Phase }
  | { k: "call"; call: Call; real: true }
  | { k: "whistle"; call: Call; by: number }
  | { k: "answer"; to: number; perfect: boolean; call: Call }
  | { k: "blunder"; id: number }
  | { k: "blundered"; to: number; why: "late" | "whistle" | "moved" }
  | { k: "pop_hit"; id: number; seat: number; name: string; cos: Cosmetics; x: number; z: number; by: number }
  | { k: "pop_miss"; id: number; x: number; z: number; by: number }
  | { k: "pop_reject"; to: number; why: "cooldown" | "charges" | "range" | "sight" | "phase" }
  | { k: "panic"; x: number; z: number }
  | { k: "grab"; stall: number; golden: boolean }
  | { k: "grabbed"; to: number; golden: boolean }
  | { k: "stash"; to: number; rarity: Rarity; golden: boolean }
  | { k: "vault"; n: number; goal: number }
  | { k: "glint"; id: number; x: number; z: number }
  | { k: "capsule"; stall: number }
  | { k: "capsule_back"; stall: number; golden: boolean }
  | { k: "respawn"; to: number; id: number; costume: number }
  | { k: "caught"; to: number }
  | { k: "bus"; state: BusState }
  | { k: "regen"; charges: number }
  | { k: "marked"; to: number; id: number }
  | { k: "round_end"; winner: Role; vault: number; seats: { seat: number; name: string; human: boolean; role: Role; stashes: number; catches: number }[] }
  | { k: "results"; to: number; role: Role; win: boolean; stashes: Rarity[]; golden: boolean; perfects: number; answered: number; blunders: number; catches: number; misses: number; popped: number; survived: boolean; whistlesIgnored: number; parade: boolean; nextInspector: boolean };

export type BusState = "away" | "arriving" | "docked" | "leaving";
export type CallState = { id: number; kind: Call; at: number; exposed: number };
export type WhistleState = { id: number; kind: Call; at: number; by: number; exposed: Set<number> };

/** per-round numbers the gates read (never on the wire) */
export type RoundMetrics = {
  n: number; winner: Role | ""; vault: number; playSec: number; parade: boolean; onboarding: boolean;
  calls: number; firstCallAt: number; whistles: number;
  callExposures: number; callAnswered: number; perfects: number; blunders: number;
  whistleExposures: number; whistleFooled: number;
  catches: number; innocentPops: number; whiffs: number; grabs: number; stashes: number;
  scriptPopAt: number; newcomerPoppedAt: number;
};
const zeroMetrics = (n: number): RoundMetrics => ({ n, winner: "", vault: 0, playSec: 0, parade: false, onboarding: false, calls: 0, firstCallAt: -1, whistles: 0, callExposures: 0, callAnswered: 0, perfects: 0, blunders: 0, whistleExposures: 0, whistleFooled: 0, catches: 0, innocentPops: 0, whiffs: 0, grabs: 0, stashes: 0, scriptPopAt: -1, newcomerPoppedAt: -1 });

const HIST = Math.ceil(T.POP_REWIND_SEC * T.SIM_HZ) + 2;

export class Sim {
  tick = 0;
  get time() { return this.tick * DT; }
  rng: Rng;
  seats: Seat[] = [];
  bodies: Body[] = [];          // on the square (incl. a caught Sneak's reveal ragdoll)
  away: Body[] = [];            // NPCs riding the bus
  events: SimEvent[] = [];      // this step's events
  // the round
  phase: Phase = "intro"; phaseAt = 0; playAt = 0; round = 0;
  vault = 0; winner: Role | "" = "";
  parade = false; onboarding = false;
  /** newcomer first-minute: their seat, the stall reserved for them, until when */
  newcomerSeat = -1; reservedStall = -1; reserveUntil = 0;
  inspectorSeat = -1; nextInspectorSeat = -1;
  // the game state
  stalls: boolean[] = M.STALLS.map(() => false);
  capsuleNextAt = 0;
  golden: 0 | 1 | 2 | 3 = 0;    // 0 none this round · 1 on the float · 2 carried · 3 stashed
  floatS = 0; float: { x: number; z: number; rot: number } | null = null;
  call: CallState | null = null; callNextAt = 0; private callSeq = 0;
  whistle: WhistleState | null = null; private whistleSeq = 0;
  panics: { x: number; z: number; until: number }[] = [];
  bus = { state: "away" as BusState, at: 0, nextAt: 0, riders: [] as Body[], exitAt: 0, leaversPicked: false };
  scriptDone = false; scriptGoal: M.V2 | null = null;
  /** poi id → uid of the body holding it (-1 free). NPC routines reserve seats, stall spots and chat spots. */
  poiOwner = new Int32Array(M.POIS.length).fill(-1);
  metrics: RoundMetrics = zeroMetrics(0);
  log: RoundMetrics[] = [];
  /** the host's wall clock (UTC ms) — only read when a round starts (the PARADE window). Deterministic in tests. */
  wallMs = 0;
  private uidSeq = 1; private usedIds = new Set<number>();

  constructor(seed = 1, opts: { wallMs?: number; names?: string[] } = {}) {
    this.rng = makeRng(seed);
    this.wallMs = opts.wallMs ?? 0;
    const names = shuffle(this.rng, [...TOWN_NAMES]);
    for (let i = 0; i < T.SEATS; i++) this.seats.push(this.newSeat(i, opts.names?.[i] || names[i % names.length]));
    this.startRound();
  }

  private newSeat(i: number, name: string): Seat {
    return {
      i, human: false, name, pid: "", cos: { ...NO_COSMETICS }, newcomer: false, role: "sneak", body: null,
      input: { mx: 0, mz: 0, sprint: false, act: false }, prevAct: false, pending: [], respawnAt: -1,
      carry: 0, grabT: 0, stashT: 0, nextGlint: 0, lookT: 0, callId: -1, callDone: true, answerAt: -1, answerPerfect: false, whistleId: -1,
      charges: T.POP_CHARGES, cooldownUntil: 0, regenAt: -1, whistleReadyAt: 0, mark: 0,
      roundsPlayed: 0, sinceInsp: 0, inRound: false, stats: zeroStats(),
    };
  }

  // ===================================================================================================================
  // Host-facing API (server, offline driver, gates). Humans and bots both come through here.

  /** a person sits down at a seat (or leaves it to a bot). A newcomer's next round is an onboarding round. */
  setHuman(i: number, human: boolean, info: { name?: string; pid?: string; cos?: Cosmetics; newcomer?: boolean } = {}) {
    const s = this.seats[i];
    s.human = human;
    if (info.name) s.name = info.name;
    if (info.pid !== undefined) s.pid = info.pid;
    if (info.cos) s.cos = { ...info.cos };
    if (human) { s.roundsPlayed = 0; s.sinceInsp = 0; s.inRound = false; s.newcomer = !!info.newcomer; s.input = { mx: 0, mz: 0, sprint: false, act: false }; s.pending = []; }
    else { s.newcomer = false; s.pid = ""; s.cos = { ...NO_COSMETICS }; }
  }
  setInput(i: number, inp: SeatInput) {
    const s = this.seats[i];
    const mx = clamp1(inp.mx), mz = clamp1(inp.mz), l = Math.hypot(mx, mz);
    s.input = { mx: l > 1 ? mx / l : mx, mz: l > 1 ? mz / l : mz, sprint: !!inp.sprint, act: !!inp.act };
  }
  /** POP: a target body id + the aim point (where the ray passed closest to that body's axis). aim null = fire at the
   *  touch MARK (validated when it was placed). Resolved inside the next step. */
  pop(i: number, id: number, aim: Vec3 | null) { this.seats[i].pending.push({ t: "pop", id: id | 0, aim: aim && finite3(aim) ? aim : null }); }
  whistleCall(i: number, call?: Call) { this.seats[i].pending.push({ t: "whistle", call }); }
  mark(i: number, id: number, aim: Vec3) { if (finite3(aim)) this.seats[i].pending.push({ t: "mark", id: id | 0, aim }); }

  /** abandon the current round and start a fresh one (no results): the host calls this when the first person sits down
   *  at a table of bots, so their first round — an onboarding round for a newcomer — is complete from its first second */
  restartRound() { this.nextInspectorSeat = -1; this.startRound(); }

  // ===================================================================================================================
  // The round

  private startRound() {
    this.round++;
    const r = this.rng;
    // who's the Inspector, and is this a newcomer's round?
    for (const s of this.seats) s.inRound = true;
    const newcomer = this.seats.find((s) => s.human && s.newcomer && s.roundsPlayed === 0);
    this.onboarding = !!newcomer; this.newcomerSeat = newcomer ? newcomer.i : -1; this.reservedStall = -1; this.reserveUntil = 0;
    const insp = this.onboarding ? this.pickBotInspector() : (this.nextInspectorSeat >= 0 && this.validNext(this.nextInspectorSeat) ? this.nextInspectorSeat : this.chooseInspector());
    this.inspectorSeat = insp; this.nextInspectorSeat = -1;
    this.parade = isParade(this.wallMs);
    this.phase = "intro"; this.phaseAt = this.time; this.playAt = this.time + T.INTRO_SEC;
    this.vault = 0; this.winner = "";
    this.call = null; this.whistle = null; this.panics = [];
    this.scriptDone = false; this.scriptGoal = null;
    this.metrics = zeroMetrics(this.round); this.metrics.parade = this.parade; this.metrics.onboarding = this.onboarding;
    // a fresh square: every body (and every id) is new
    this.bodies = []; this.away = []; this.usedIds.clear(); this.poiOwner.fill(-1);
    this.bus = { state: "away", at: this.time, nextAt: this.playAt + range(r, 4, T.BUS_EVERY), riders: [], exitAt: 0, leaversPicked: false };
    // costumes: every resident type twice, the rest at random → 2–3 copies each
    const costumes: number[] = [];
    for (let k = 0; k < T.NPC_COUNT; k++) costumes.push(k < RESIDENTS.length * 2 ? k % RESIDENTS.length : int(r, 0, RESIDENTS.length - 1));
    shuffle(r, costumes);
    const spots = this.spawnSpots(T.NPC_COUNT + T.SEATS);
    let sp = 0;
    for (let k = 0; k < T.NPC_COUNT; k++) {
      const b = this.newBody("npc", -1, costumes[k]);
      if (k < T.NPC_AWAY_START) { b.visible = false; this.away.push(b); continue; }
      b.x = spots[sp].x; b.z = spots[sp].z; b.h = range(r, -Math.PI, Math.PI); sp++;
      b.brain = newBrain(); this.bodies.push(b);
    }
    // seats: the Inspector by the bandstand; Sneaks scattered among the townsfolk in lookalike costumes
    const used = new Set<number>();
    for (const s of this.seats) {
      s.role = s.i === insp ? "inspector" : "sneak";
      s.carry = 0; s.grabT = 0; s.stashT = 0; s.respawnAt = -1; s.pending = []; s.callId = -1; s.callDone = true; s.whistleId = -1;
      s.charges = T.POP_CHARGES; s.cooldownUntil = 0; s.regenAt = -1; s.whistleReadyAt = this.playAt + 5; s.mark = 0;
      s.stats = zeroStats();
      if (s.role === "inspector") {
        const b = this.newBody("inspector", s.i, INSPECTOR_COSTUME);
        b.x = M.BANDSTAND.x; b.z = M.BANDSTAND.z + M.BANDSTAND.r + 0.9; b.h = 0; s.body = b; this.bodies.push(b);
      } else {
        const b = this.newBody("sneak", s.i, this.sneakCostume(-1, used)); used.add(b.costume);
        b.x = spots[sp].x; b.z = spots[sp].z; b.h = range(r, -Math.PI, Math.PI); sp++;
        s.body = b; this.bodies.push(b);
      }
    }
    // capsules on the stalls
    this.stalls = this.stalls.map(() => false);
    for (const k of shuffle(r, M.STALLS.map((s) => s.i)).slice(0, this.onboarding ? T.ONBOARD_CAPSULES_START : T.CAPSULES_START)) this.stalls[k] = true;
    this.capsuleNextAt = this.playAt + T.CAPSULE_RESPAWN;
    // newcomer first minute: step off right in front of a stocked stall, facing it; its capsule is reserved for them
    if (this.onboarding && this.newcomerSeat >= 0) {
      const nb = this.seats[this.newcomerSeat].body!;
      const stocked = M.STALLS.filter((st) => this.stalls[st.i]);
      const st = stocked.length ? stocked[int(r, 0, stocked.length - 1)] : M.STALLS[0];
      this.stalls[st.i] = true;
      const fx = Math.sin(st.rot), fz = Math.cos(st.rot);   // the stall's front direction (spots sit at +D/2 along it)
      let px = st.capsule.x + fx * T.ONBOARD_SPAWN_DIST, pz = st.capsule.z + fz * T.ONBOARD_SPAWN_DIST;
      if (M.blockedAt(px, pz)) { const sp2 = st.spots[1]; px = sp2.x + fx * 1.5; pz = sp2.z + fz * 1.5; }
      nb.x = px; nb.z = pz; nb.h = M.headingTo(px, pz, st.capsule.x, st.capsule.z);
      this.reservedStall = st.i; this.reserveUntil = this.playAt + T.ONBOARD_RESERVE;
    }
    // the parade: the float, the Golden Capsule on it, and a conga line behind it
    this.golden = 0; this.float = null;
    if (this.parade) {
      this.golden = 1; this.floatS = range(r, 0, M.FLOAT_LOOP_LEN); this.float = M.floatAt(this.floatS);
      const congers = shuffle(r, this.bodies.filter((b) => b.kind === "npc")).slice(0, T.CONGA_N);
      congers.forEach((b, k) => { b.brain!.task = "conga"; b.brain!.slot = k; const p = M.floatAt(this.floatS - (T.FLOAT_R + 0.8 + k * T.CONGA_GAP)); b.x = p.x * 0.97; b.z = p.z * 0.97; });
    }
    // the calls: never in the first 6 s; a newcomer's round has its first at exactly 6 s
    this.callNextAt = this.playAt + (this.onboarding ? T.ONBOARD_FIRST_CALL_MAX : range(r, T.CALL_FIRST_MIN, T.CALL_FIRST_MAX));
    for (const b of this.bodies) { b.hist.fill(0); b.histN = 0; this.pushHist(b); if (b.brain && b.brain.task !== "conga") npcPlan(this, b); }
    this.events.push({ k: "round_start", n: this.round, parade: this.parade, onboarding: this.onboarding, inspector: this.seats[insp].body!.id, inspectorSeat: insp });
    this.events.push({ k: "phase", phase: "intro" });
  }

  /** DESIGN §2 role rotation. A person's first round is always a Sneak round; the Inspector seat goes to the person who
   *  has waited longest (≥ INSPECTOR_MIN_GAP rounds since their last turn); alone at the table: every 3rd round. */
  private chooseInspector(): number {
    const humans = this.seats.filter((s) => s.human);
    if (humans.length === 1) {
      const h = humans[0];
      if (h.roundsPlayed >= 1 && (h.roundsPlayed + 1) % T.INSPECTOR_EVERY_SOLO === 0) return h.i;
    } else if (humans.length > 1) {
      const ok = humans.filter((s) => s.roundsPlayed >= 1 && s.sinceInsp >= T.INSPECTOR_MIN_GAP).sort((a, b) => b.sinceInsp - a.sinceInsp || a.i - b.i);
      if (ok.length) return ok[0].i;
    }
    return this.pickBotInspector();
  }
  private pickBotInspector(): number {
    const bots = this.seats.filter((s) => !s.human).sort((a, b) => b.sinceInsp - a.sinceInsp || a.i - b.i);
    return bots.length ? bots[0].i : this.seats[0].i;
  }
  private validNext(i: number) { const s = this.seats[i]; return !!s && (s.human ? s.roundsPlayed >= 1 : true) && !this.seats.some((q) => q.human && q.newcomer && q.roundsPlayed === 0); }

  private endRound(winner: Role) {
    this.winner = winner; this.phase = "results"; this.phaseAt = this.time;
    this.call = null; this.whistle = null;
    const m = this.metrics; m.winner = winner; m.vault = this.vault; m.playSec = this.time - this.playAt;
    this.log.push(m);
    for (const s of this.seats) if (s.inRound) { s.roundsPlayed++; s.sinceInsp = s.role === "inspector" ? 0 : s.sinceInsp + 1; }
    // decide the next Inspector now, so the results card can say "NEXT ROUND: YOU ARE THE INSPECTOR"
    this.nextInspectorSeat = this.chooseInspector();
    this.events.push({ k: "phase", phase: "results" });
    this.events.push({ k: "round_end", winner, vault: this.vault, seats: this.seats.map((s) => ({ seat: s.i, name: s.name, human: s.human, role: s.role, stashes: s.stats.stashes.length, catches: s.stats.catches })) });
    for (const s of this.seats) {
      const st = s.stats;
      this.events.push({ k: "results", to: s.i, role: s.role, win: s.role === winner, stashes: [...st.stashes], golden: st.golden, perfects: st.perfects, answered: st.answered, blunders: st.blunders, catches: st.catches, misses: st.misses, popped: st.popped, survived: s.role === "sneak" && st.popped === 0, whistlesIgnored: st.whistlesIgnored, parade: this.parade, nextInspector: this.nextInspectorSeat === s.i && s.human });
      if (s.newcomer && s.inRound) s.newcomer = false;
    }
    for (const b of this.bodies) { b.vx = b.vz = 0; if (b.mode === "call" || b.mode === "panic") { b.mode = "free"; b.callAnim = null; } }
    const ib = this.seats[this.inspectorSeat]?.body; if (ib && winner === "inspector") this.setAnim(ib, "cheer");
  }

  // ===================================================================================================================
  // The step

  step() {
    this.tick++; this.events = [];
    const now = this.time;
    if (this.phase === "intro" && now >= this.playAt) { this.phase = "play"; this.phaseAt = now; this.events.push({ k: "phase", phase: "play" }); }
    if (this.phase === "results" && now - this.phaseAt >= T.RESULTS_SEC) { this.startRound(); return; }
    if (this.phase === "play") {
      if (now - this.playAt >= T.ROUND_SEC) { this.endRound("inspector"); return; }
      this.stepCalls(now); this.stepCapsules(now); this.stepScript(now);
      for (const s of this.seats) if (s.role === "inspector") this.stepRegen(s, now);
    }
    this.panics = this.panics.filter((p) => p.until > now);
    this.stepBus(now);
    if (this.float && this.phase !== "results") { this.floatS += T.FLOAT_SPEED * DT; this.float = M.floatAt(this.floatS); }
    for (const s of this.seats) this.stepSeat(s, now);
    for (const b of this.bodies) if (b.brain) npcThink(this, b, now);
    this.stepModes(now);
    this.integrate();
    for (const s of this.seats) this.stepGlint(s, now);
    this.bodies = this.bodies.filter((b) => !(b.removeAt > 0 && now >= b.removeAt));
    for (const b of this.bodies) this.pushHist(b);
    if (this.phase === "play" && this.vault >= T.VAULT_GOAL) this.endRound("sneak");
  }

  // ---- calls & whistles --------------------------------------------------------------------------------------------
  private stepCalls(now: number) {
    const r = this.rng;
    if (!this.call && now >= this.callNextAt) {
      const kind = pick(r, CALLS);
      this.call = { id: ++this.callSeq, kind, at: now, exposed: 0 };
      this.callNextAt = now + range(r, T.CALL_GAP_MIN, T.CALL_GAP_MAX);
      this.events.push({ k: "call", call: kind, real: true });
      this.metrics.calls++; if (this.metrics.firstCallAt < 0) this.metrics.firstCallAt = now - this.playAt;
      // every townie reacts after their own stagger (the PERFECT band is this band)
      for (const b of this.bodies) if (b.brain && callable(b)) { b.brain.callAt = now + range(r, T.CALL_PERFECT_LO, T.CALL_PERFECT_HI); b.brain.callKind = kind; }
      for (const s of this.seats) if (s.role === "sneak" && s.body && callable(s.body)) { s.callId = this.call.id; s.callDone = false; s.answerAt = -1; this.call.exposed++; this.metrics.callExposures++; }
    }
    if (this.call && now - this.call.at > T.CALL_WINDOW + T.CALL_HOLD + T.CALL_PERFECT_HI + 0.2) this.call = null;
    const w = this.whistle;
    if (w && now - w.at > T.WHISTLE_WINDOW) {
      for (const i of w.exposed) { const s = this.seats[i]; if (s.whistleId !== w.id) s.stats.whistlesIgnored++; }
      this.whistle = null;
    }
  }
  private callWindowOpen(now: number) { return !!this.call && now - this.call.at <= T.CALL_WINDOW; }

  private blunder(s: Seat, b: Body, why: "late" | "whistle" | "moved") {
    b.blunderUntil = this.time + T.BLUNDER_SEC;
    s.stats.blunders++; this.metrics.blunders++;
    this.events.push({ k: "blunder", id: b.id }, { k: "blundered", to: s.i, why });
  }

  // ---- capsules ----------------------------------------------------------------------------------------------------
  private stepCapsules(now: number) {
    if (now < this.capsuleNextAt) return;
    this.capsuleNextAt = now + T.CAPSULE_RESPAWN;
    const empty = this.stalls.map((h, i) => (h ? -1 : i)).filter((i) => i >= 0);
    if (!empty.length || this.stalls.filter(Boolean).length >= T.CAPSULES_MAX) return;
    const k = pick(this.rng, empty); this.stalls[k] = true;
    this.events.push({ k: "capsule", stall: k });
  }
  private dropCapsule(s: Seat, b: Body) {
    if (s.carry === 2) { this.golden = 1; this.events.push({ k: "capsule_back", stall: -1, golden: true }); }
    else if (s.carry === 1) {
      let best = -1, bd = Infinity;
      for (const st of M.STALLS) if (!this.stalls[st.i]) { const d = Math.hypot(st.capsule.x - b.x, st.capsule.z - b.z); if (d < bd) { bd = d; best = st.i; } }
      if (best >= 0) { this.stalls[best] = true; this.events.push({ k: "capsule_back", stall: best, golden: false }); }
    }
    s.carry = 0; s.grabT = 0; s.stashT = 0;
  }

  // ---- the onboarding script: the gentle bot Inspector pops an innocent around 30 s ("OOPS! He has 2 POPS left.") --
  private stepScript(now: number) {
    if (!this.onboarding || this.scriptDone) return;
    const t = now - this.playAt; if (t < T.ONBOARD_SCRIPT_POP) return;
    const s = this.seats[this.inspectorSeat]; const ib = s.body; if (!ib || s.human) { this.scriptDone = true; return; }
    let best: Body | null = null, bd = Infinity;
    for (const b of this.bodies) {
      if (b.kind !== "npc" || !b.visible || b.mode !== "free" || b.anchored) continue;
      const d = Math.hypot(b.x - ib.x, b.z - ib.z);
      if (d < bd && d <= T.POP_RANGE - 2 && M.losClear(ib.x, ib.z, b.x, b.z)) { bd = d; best = b; }
    }
    if (best && now >= s.cooldownUntil && s.charges > 0) {
      this.pop(s.i, best.id, { x: best.x, y: 0.8, z: best.z });
      this.scriptDone = true; this.scriptGoal = null; this.metrics.scriptPopAt = t;
    } else {   // nobody in reach yet: walk toward the nearest townie
      let near: Body | null = null, nd = Infinity;
      for (const b of this.bodies) if (b.kind === "npc" && b.visible) { const d = Math.hypot(b.x - ib.x, b.z - ib.z); if (d < nd) { nd = d; near = b; } }
      this.scriptGoal = near ? { x: near.x, z: near.z } : null;
    }
  }

  // ---- the Inspector's charges -------------------------------------------------------------------------------------
  private stepRegen(s: Seat, now: number) {
    if (s.charges >= T.POP_CHARGES) { s.regenAt = -1; return; }
    if (s.regenAt < 0) s.regenAt = now + T.POP_REGEN_SEC;
    else if (now >= s.regenAt) { s.charges++; s.regenAt = s.charges < T.POP_CHARGES ? now + T.POP_REGEN_SEC : -1; this.events.push({ k: "regen", charges: s.charges }); }
  }

  // ---- the bus -----------------------------------------------------------------------------------------------------
  private stepBus(now: number) {
    const bus = this.bus, r = this.rng;
    const respawnSoon = this.seats.some((s) => s.respawnAt >= 0 && now >= s.respawnAt - T.BUS_DRIVE);
    // pick who leaves on the next timetable bus, early enough for them to walk to the stop
    if (!bus.leaversPicked && this.phase !== "results" && now >= bus.nextAt - 12) {
      bus.leaversPicked = true;
      const n = this.away.length < 2 ? 3 : int(r, 1, 3);
      const pool = shuffle(r, this.bodies.filter((b) => b.kind === "npc" && b.brain && b.mode === "free" && b.brain.task !== "conga" && !b.brain.partner && !b.brain.leaving));
      // a resident type never drops below two townsfolk on the square: a Sneak wearing it must always have lookalikes
      const count = new Array(RESIDENTS.length).fill(0);
      for (const b of this.bodies) if (b.kind === "npc" && b.visible && !b.brain?.leaving) count[b.costume]++;
      let picked = 0;
      for (const b of pool) {
        if (picked >= n) break;
        if (count[b.costume] <= 2) continue;
        count[b.costume]--; picked++;
        npcRelease(this, b); b.brain!.leaving = true; npcPlan(this, b);
      }
    }
    if (bus.state === "away" && (now >= bus.nextAt || respawnSoon) && this.phase !== "results") {
      bus.state = "arriving"; bus.at = now;
      const drop = Math.min(this.away.length, int(r, T.BUS_DROP_MIN, T.BUS_DROP_MAX));
      bus.riders = shuffle(r, [...this.away]).slice(0, drop);
      this.events.push({ k: "bus", state: "arriving" });
    } else if (bus.state === "arriving" && now - bus.at >= T.BUS_DRIVE) {
      bus.state = "docked"; bus.at = now; bus.exitAt = now + 0.3;
      this.events.push({ k: "bus", state: "docked" });
    } else if (bus.state === "docked") {
      // riders step off one at a time: townsfolk and any Sneak whose respawn is due, in a shuffled order
      if (now >= bus.exitAt) {
        const due = this.seats.filter((s) => s.respawnAt >= 0 && now >= s.respawnAt);
        const total = bus.riders.length + due.length;
        if (total > 0) {
          bus.exitAt = now + T.BUS_EXIT_GAP;
          if (due.length && (bus.riders.length === 0 || r() < due.length / total)) this.respawnSneak(due[0]);
          else { const b = bus.riders.shift()!; this.away = this.away.filter((x) => x !== b); this.arrive(b); }
        }
      }
      // townsfolk waiting at the stop climb aboard
      for (const b of this.bodies) if (b.brain?.leaving && b.brain.task === "board" && Math.hypot(b.x - M.BUS.door.x, b.z - M.BUS.door.z) < 0.6) this.board(b);
      const waiting = this.seats.some((s) => s.respawnAt >= 0 && now >= s.respawnAt - 1);
      if (now - bus.at >= T.BUS_DOCK && !bus.riders.length && !waiting) { bus.state = "leaving"; bus.at = now; this.events.push({ k: "bus", state: "leaving" }); }
    } else if (bus.state === "leaving" && now - bus.at >= T.BUS_DRIVE) {
      bus.state = "away"; bus.at = now; bus.nextAt = now + T.BUS_EVERY - 2 * T.BUS_DRIVE - T.BUS_DOCK; bus.leaversPicked = false;
      this.events.push({ k: "bus", state: "away" });
    }
  }
  /** a townsperson steps off the bus: a NEW wire id (nobody can follow a body through a bus trip) */
  private arrive(b: Body) {
    b.id = this.allocId(); b.visible = true; b.mode = "free"; b.anchored = false; b.callAnim = null;
    b.x = M.BUS.door.x + range(this.rng, -0.2, 0.2); b.z = M.BUS.door.z; b.h = Math.PI; b.vx = b.vz = 0;
    b.blunderUntil = b.glintUntil = b.panicUntil = 0; b.histN = 0;
    b.brain = newBrain(); this.setAnim(b, "walk"); this.bodies.push(b); npcPlan(this, b);
  }
  private board(b: Body) {
    npcRelease(this, b); b.visible = false; b.brain = null; this.bodies = this.bodies.filter((x) => x !== b); this.away.push(b);
  }
  private respawnSneak(s: Seat) {
    const prev = s.body?.costume ?? -1;
    const b = this.newBody("sneak", s.i, this.sneakCostume(prev, new Set()));
    b.x = M.BUS.door.x + range(this.rng, -0.2, 0.2); b.z = M.BUS.door.z; b.h = Math.PI;
    s.body = b; s.respawnAt = -1; s.carry = 0; s.grabT = 0; s.stashT = 0; s.callDone = true; s.prevAct = s.input.act;
    this.bodies.push(b); this.pushHist(b);
    this.events.push({ k: "respawn", to: s.i, id: b.id, costume: b.costume });
  }

  // ---- seats: the Sneak's and the Inspector's verbs ----------------------------------------------------------------
  private stepSeat(s: Seat, now: number) {
    const b = s.body;
    const inp = s.input, actDown = inp.act && !s.prevAct; s.prevAct = inp.act;
    const actions = s.pending; s.pending = [];
    if (!b || b.mode === "popped" || !b.visible) return;
    if (this.phase !== "play") { b.vx = b.vz = 0; if (this.phase === "intro" && b.anim !== "idle") this.setAnim(b, "idle"); return; }
    if (s.role === "sneak") this.sneakControl(s, b, now, actDown);
    else this.inspectorControl(s, b, now, actions);
  }

  private sneakControl(s: Seat, b: Body, now: number, actDown: boolean) {
    const inp = s.input, moving = Math.hypot(inp.mx, inp.mz) > T.MOVE_DEADZONE;
    const c = this.call;
    // ---- the call window
    if (c && s.callId === c.id && !s.callDone) {
      const el = now - c.at;
      if (el <= T.CALL_WINDOW) {
        if (c.kind === "freeze" ? actDown || (!moving && el >= T.CALL_PERFECT_LO) : actDown) { this.answer(s, b, c, el); actDown = false; }
      } else { s.callDone = true; this.blunder(s, b, "late"); }
    }
    // ---- a whistle: answering it is the blunder (NPCs ignore it; only the bell is real)
    const w = this.whistle;
    if (w && actDown && w.exposed.has(s.i) && s.whistleId !== w.id && now - w.at <= T.WHISTLE_WINDOW && !this.callWindowOpen(now) && b.mode === "free") {
      s.whistleId = w.id; s.stats.whistlesFooled++; this.metrics.whistleFooled++;
      this.blunder(s, b, "whistle");
      this.enterCall(b, CALL_ANIM[w.kind], now + T.WHISTLE_FOOL_HOLD, false);
      s.grabT = s.stashT = 0; actDown = false;
    }
    // ---- performing a call: locked in place (FREEZE: moving is the blunder that breaks it)
    if (b.mode === "call") {
      if (b.freezeCall && moving) { b.mode = "free"; b.callAnim = null; b.freezeCall = false; this.blunder(s, b, "moved"); }
      else { b.vx = b.vz = 0; return; }
    }
    // ---- grab (looks like browsing) / stash (looks like tying a shoelace)
    let busy = false;
    if (inp.act && !moving) {
      if (s.carry === 0) {
        const st = M.stallNear(b.x, b.z, T.GRAB_RANGE);
        const gold = this.golden === 1 && this.float && Math.hypot(b.x - this.float.x, b.z - this.float.z) <= T.GOLDEN_GRAB_RANGE;
        const held = st >= 0 && st === this.reservedStall && s.i !== this.newcomerSeat && now < this.reserveUntil;   // the newcomer's capsule
        if ((st >= 0 && this.stalls[st] && !held) || gold) {
          busy = true; s.grabT += DT; s.stashT = 0; this.setAnim(b, "browse");
          b.face = st >= 0 && this.stalls[st] ? M.headingTo(b.x, b.z, M.STALLS[st].capsule.x, M.STALLS[st].capsule.z) : M.headingTo(b.x, b.z, this.float!.x, this.float!.z);
          const firstTime = this.onboarding && s.i === this.newcomerSeat && s.stats.grabs === 0;
          if (s.grabT >= (firstTime ? T.ONBOARD_GRAB_SEC : T.GRAB_SEC) - 1e-9) {
            s.grabT = 0;
            if (st >= 0 && this.stalls[st]) { this.stalls[st] = false; s.carry = 1; } else { this.golden = 2; s.carry = 2; }
            if (s.i === this.newcomerSeat) this.reserveUntil = 0;   // theirs now: the reservation ends
            s.stats.grabs++; this.metrics.grabs++;
            b.glintUntil = 0; s.nextGlint = now + T.GLINT_EVERY;
            this.events.push({ k: "grab", stall: s.carry === 2 ? -1 : st, golden: s.carry === 2 }, { k: "grabbed", to: s.i, golden: s.carry === 2 });
          }
        }
      } else {
        const d = M.drainNear(b.x, b.z, T.STASH_RANGE);
        const botWait = this.onboarding && !s.human && now < this.playAt + T.ONBOARD_BOT_STASH_AFTER;   // the newcomer's stash comes first
        if (d >= 0 && !botWait) {
          busy = true; s.stashT += DT; s.grabT = 0; this.setAnim(b, "shoelace");
          const firstStash = this.onboarding && s.i === this.newcomerSeat && s.stats.stashes.length === 0;
          if (s.stashT >= (firstStash ? T.ONBOARD_STASH_SEC : T.STASH_SEC) - 1e-9) this.stash(s, b);
        }
      }
    }
    if (!busy) { s.grabT = 0; s.stashT = 0; }
    if (busy) { b.vx = b.vz = 0; return; }
    // ---- walking / sprinting
    const speed = inp.sprint ? T.SPRINT : T.WALK;
    if (moving) { b.face = null; this.setAnim(b, inp.sprint ? "run" : "walk"); }
    else if (b.anim === "walk" || b.anim === "run" || (b.anim === "browse" && this.animAge(b) > 1.0) || (b.anim === "shoelace" && this.animAge(b) > T.SHOELACE_SEC) || ANIM_IS_CALL[b.anim]) { this.setAnim(b, "idle"); s.lookT = now + range(this.rng, 1.5, 3.5); }
    else if ((b.anim === "idle" || b.anim === "look_around") && now >= s.lookT) {
      // standing about, a Sneak glances round on the same rhythm as the townsfolk (a body that never looks around would be a tell)
      s.lookT = now + range(this.rng, 1.5, 3.5); this.setAnim(b, b.anim === "idle" ? "look_around" : "idle");
    }
    const v = moveVelocity(inp.mx, inp.mz, speed); b.vx = v.x; b.vz = v.z;
  }

  private answer(s: Seat, b: Body, c: CallState, el: number) {
    s.callDone = true; s.answerAt = el;
    const perfect = el >= T.CALL_PERFECT_LO && el <= T.CALL_PERFECT_HI;
    s.answerPerfect = perfect; s.stats.answered++; this.metrics.callAnswered++;
    if (perfect) { s.stats.perfects++; this.metrics.perfects++; }
    s.grabT = 0; s.stashT = 0;   // answering cancels a grab / stash in progress
    this.enterCall(b, CALL_ANIM[c.kind], this.time + T.CALL_HOLD, c.kind === "freeze");
    this.events.push({ k: "answer", to: s.i, perfect, call: c.kind });
  }
  enterCall(b: Body, anim: Anim, until: number, freeze: boolean) {
    b.mode = "call"; b.modeAt = this.time; b.modeUntil = until; b.callAnim = anim; b.freezeCall = freeze; b.vx = b.vz = 0;
    this.setAnim(b, anim);
  }

  private stash(s: Seat, b: Body) {
    const golden = s.carry === 2;
    s.stashT = 0; s.carry = 0;
    const rarity: Rarity = golden ? "legendary" : T.RARITY[weighted(this.rng, T.RARITY.map((x) => x.w))].id;
    if (golden) { this.golden = 3; s.stats.golden = true; }
    s.stats.stashes.push(rarity); this.metrics.stashes++;
    this.vault++;
    // the newcomer's first stash: the Crier's first call follows shortly (it waited so it would not interrupt the grab)
    if (this.onboarding && s.i === this.newcomerSeat && s.stats.stashes.length === 1 && this.metrics.calls === 0) this.callNextAt = Math.min(this.callNextAt, this.time + 3);
    this.events.push({ k: "stash", to: s.i, rarity, golden }, { k: "vault", n: this.vault, goal: T.VAULT_GOAL });
  }

  private stepGlint(s: Seat, now: number) {
    const b = s.body; if (!b || s.carry === 0 || b.mode === "popped" || this.phase !== "play") return;
    if (now >= s.nextGlint) {
      s.nextGlint = now + T.GLINT_EVERY; b.glintUntil = now + T.GLINT_FLASH;
      this.events.push({ k: "glint", id: b.id, x: r2(b.x), z: r2(b.z) });
    }
  }

  private inspectorControl(s: Seat, b: Body, now: number, actions: SeatAction[]) {
    for (const a of actions) {
      if (a.t === "pop") this.resolvePop(s, b, now, a.id, a.aim);
      else if (a.t === "whistle") this.doWhistle(s, b, now, a.call);
      else if (a.t === "mark") { const t = this.fits(a.id, a.aim, b) ; s.mark = t ? t.id : 0; this.events.push({ k: "marked", to: s.i, id: s.mark }); }
    }
    const inp = s.input, moving = Math.hypot(inp.mx, inp.mz) > T.MOVE_DEADZONE;
    const v = moveVelocity(inp.mx, inp.mz, T.INSPECTOR_WALK); b.vx = v.x; b.vz = v.z;
    if (b.anim === "point" && this.animAge(b) < T.POINT_SEC) return;
    if (moving) b.face = null;
    this.setAnim(b, moving ? "walk" : "idle");
  }

  private doWhistle(s: Seat, b: Body, now: number, call?: Call) {
    if (now < s.whistleReadyAt || this.callWindowOpen(now) || this.whistle) return;
    const kind = call && CALLS.includes(call) && call !== "freeze" ? call : pick(this.rng, CALLS.filter((c) => c !== "freeze"));
    s.whistleReadyAt = now + T.WHISTLE_COOLDOWN;
    const exposed = new Set<number>();
    for (const q of this.seats) if (q.role === "sneak" && q.body && callable(q.body)) { exposed.add(q.i); q.stats.whistlesHeard++; }
    this.whistle = { id: ++this.whistleSeq, kind, at: now, by: b.id, exposed };
    this.metrics.whistles++; this.metrics.whistleExposures += exposed.size;
    this.events.push({ k: "whistle", call: kind, by: b.id });
  }

  /** which body does this aim point hit? The claimed id if the point lies in its pick cylinder at any moment of the
   *  last POP_REWIND_SEC (what the shooter saw, interpolated), else any other body it lies in, else nobody. */
  private fits(id: number, aim: Vec3, shooter: Body): Body | null {
    const R = T.POP_PICK_R + T.POP_SLACK;
    if (aim.y < -0.2 || aim.y > T.POP_BODY_H + T.POP_PICK_R) return null;
    const inside = (b: Body) => { for (let k = 0; k < b.histN; k++) if (Math.hypot(aim.x - b.hist[k * 2], aim.z - b.hist[k * 2 + 1]) <= R) return true; return Math.hypot(aim.x - b.x, aim.z - b.z) <= R; };
    const ok = (b: Body) => b !== shooter && b.visible && b.mode !== "popped";
    const claimed = this.bodies.find((b) => b.id === id && ok(b));
    if (claimed && inside(claimed)) return claimed;
    let best: Body | null = null, bd = Infinity;
    for (const b of this.bodies) if (ok(b) && inside(b)) { const d = Math.hypot(aim.x - b.x, aim.z - b.z); if (d < bd) { bd = d; best = b; } }
    return best;
  }

  private resolvePop(s: Seat, ib: Body, now: number, id: number, aim: Vec3 | null) {
    const reject = (why: "cooldown" | "charges" | "range" | "sight" | "phase") => this.events.push({ k: "pop_reject", to: s.i, why });
    if (now < s.cooldownUntil) return reject("cooldown");
    if (s.charges <= 0) return reject("charges");
    let target: Body | null;
    if (!aim) { target = s.mark && s.mark === id ? this.bodies.find((b) => b.id === id && b.visible && b.mode !== "popped") || null : null; aim = target ? { x: target.x, y: 0.8, z: target.z } : null; }
    else target = this.fits(id, aim, ib);
    if (!aim) return reject("range");
    const tx = target ? target.x : aim.x, tz = target ? target.z : aim.z;
    if (Math.hypot(tx - ib.x, tz - ib.z) > T.POP_RANGE + T.POP_PICK_R) return reject("range");
    if (!M.losClear(ib.x, ib.z, tx, tz)) return reject("sight");
    s.cooldownUntil = now + T.POP_COOLDOWN; s.mark = 0;
    this.setAnim(ib, "point"); ib.face = M.headingTo(ib.x, ib.z, tx, tz);
    if (!target) { s.stats.whiffs++; this.metrics.whiffs++; this.events.push({ k: "pop_miss", id: -1, x: r2(aim.x), z: r2(aim.z), by: ib.id }); return; }
    s.charges--;
    const victimSeat = target.kind === "sneak" ? this.seats[target.seat] : null;
    if (victimSeat) {
      // CATCH: the costume bursts, the true self flies; the capsule goes back; a new costume arrives on the bus later
      s.charges = Math.min(T.POP_CHARGES, s.charges + 1); s.stats.catches++; this.metrics.catches++;
      victimSeat.stats.popped++;
      if (victimSeat.newcomer && this.metrics.newcomerPoppedAt < 0) this.metrics.newcomerPoppedAt = now - this.playAt;
      this.dropCapsule(victimSeat, target);
      target.mode = "popped"; target.modeAt = now; target.revealed = true; target.removeAt = now + T.REVEAL_SEC; target.vx = target.vz = 0; target.callAnim = null;
      this.setAnim(target, "ragdoll");
      victimSeat.respawnAt = now + T.RESPAWN_SEC;
      this.events.push({ k: "pop_hit", id: target.id, seat: victimSeat.i, name: victimSeat.name, cos: { ...victimSeat.cos }, x: r2(target.x), z: r2(target.z), by: ib.id }, { k: "caught", to: victimSeat.i });
    } else {
      // MISS: an innocent tumbles, gets up angry, and everyone near runs
      s.stats.misses++; this.metrics.innocentPops++;
      if (target.brain) npcRelease(this, target);
      target.mode = "tumble"; target.modeAt = now; target.modeUntil = now + T.TUMBLE_SEC + T.GETUP_SEC + T.ANGRY_SEC; target.vx = target.vz = 0; target.callAnim = null;
      if (target.brain) target.brain.callAt = 0;
      this.setAnim(target, "ragdoll");
      this.panicAt(target.x, target.z, target);
      this.events.push({ k: "pop_miss", id: target.id, x: r2(target.x), z: r2(target.z), by: ib.id });
    }
    if (s.charges < T.POP_CHARGES && s.regenAt < 0) s.regenAt = now + T.POP_REGEN_SEC;
  }

  /** PANIC: every townie within PANIC_R runs from the pop for PANIC_SEC; Sneaks inside it may sprint unseen */
  private panicAt(x: number, z: number, except: Body) {
    const now = this.time;
    this.panics.push({ x, z, until: now + T.PANIC_SEC });
    this.events.push({ k: "panic", x: r2(x), z: r2(z) });
    for (const b of this.bodies) {
      if (b === except || !b.visible || b.mode === "popped" || b.mode === "tumble") continue;
      if (Math.hypot(b.x - x, b.z - z) > T.PANIC_R) continue;
      b.panicUntil = now + T.PANIC_SEC;
      if (b.kind !== "npc" || !b.brain) continue;
      npcRelease(this, b); b.brain.callAt = 0;
      b.mode = "panic"; b.modeAt = now; b.modeUntil = now + T.PANIC_SEC; b.callAnim = null;
      const d = Math.hypot(b.x - x, b.z - z) || 1; b.brain.fleeX = (b.x - x) / d; b.brain.fleeZ = (b.z - z) / d;
      if (d < 0.05) { const a = this.rng() * Math.PI * 2; b.brain.fleeX = Math.cos(a); b.brain.fleeZ = Math.sin(a); }
      this.setAnim(b, "run");
    }
  }

  // ---- body modes: calls end, tumbles progress, panics end -----------------------------------------------------------
  private stepModes(now: number) {
    for (const b of this.bodies) {
      if (b.mode === "call" && now >= b.modeUntil) {
        b.mode = "free"; b.callAnim = null; b.freezeCall = false;
        if (b.brain) npcResumeAfterCall(this, b); else this.setAnim(b, "idle");
      } else if (b.mode === "tumble") {
        const t = now - b.modeAt;
        if (t >= T.TUMBLE_SEC + T.GETUP_SEC + T.ANGRY_SEC) { b.mode = "free"; if (b.brain) npcPlan(this, b); else this.setAnim(b, "idle"); }
        else if (t >= T.TUMBLE_SEC + T.GETUP_SEC) this.setAnim(b, "angry");
        else if (t >= T.TUMBLE_SEC) this.setAnim(b, "getup");
      } else if (b.mode === "panic" && now >= b.modeUntil) {
        b.mode = "free"; b.vx = b.vz = 0; if (b.brain) npcPlan(this, b);
      }
    }
  }

  // ---- movement: velocities → positions, then bodies push apart, then the static world pushes back -----------------
  private integrate() {
    const bs = this.bodies, R = T.BODY_R;
    for (const b of bs) {
      if (!b.visible || b.anchored || b.mode === "popped" || b.mode === "tumble" || b.mode === "call") { b.vx = b.vz = 0; this.turn(b); continue; }
      if (this.phase === "results") { b.vx = b.vz = 0; }
      const p = M.collideStatic(b.x + b.vx * DT, b.z + b.vz * DT, R);
      b.x = p.x; b.z = p.z;
      this.turn(b);
    }
    // bodies push apart; one that stands (sitting, performing, ragdolling) does not get shoved
    for (let i = 0; i < bs.length; i++) {
      const a = bs[i]; if (!a.visible) continue;
      for (let j = i + 1; j < bs.length; j++) {
        const b = bs[j]; if (!b.visible) continue;
        const dx = b.x - a.x, dz = b.z - a.z; if (Math.abs(dx) > 2 * R || Math.abs(dz) > 2 * R) continue;
        const d = Math.hypot(dx, dz); if (d >= 2 * R) continue;
        const fa = fixedBody(a), fb = fixedBody(b); if (fa && fb) continue;
        const nx = d > 1e-6 ? dx / d : 1, nz = d > 1e-6 ? dz / d : 0, over = 2 * R - d;
        const wa = fa ? 0 : fb ? 1 : 0.5, wb = 1 - wa;
        a.x -= nx * over * wa; a.z -= nz * over * wa; b.x += nx * over * wb; b.z += nz * over * wb;
      }
    }
    for (const b of bs) {
      if (!b.visible || fixedBody(b)) continue;
      if (this.float) { const dx = b.x - this.float.x, dz = b.z - this.float.z, d = Math.hypot(dx, dz), min = T.FLOAT_R + R; if (d < min && d > 1e-6) { b.x = this.float.x + (dx / d) * min; b.z = this.float.z + (dz / d) * min; } }
      const p = M.collideStatic(b.x, b.z, R); b.x = p.x; b.z = p.z;
    }
  }
  private turn(b: Body) {
    const sp = Math.hypot(b.vx, b.vz);
    const want = sp > 0.05 ? Math.atan2(b.vx, b.vz) : b.face;
    if (want === null || want === undefined) return;
    b.h = slewAngle(b.h, want, T.TURN_RATE * DT);
  }

  // ===================================================================================================================
  // Helpers

  setAnim(b: Body, a: Anim) { if (b.anim !== a) { b.anim = a; b.animAt = this.tick; } }
  animAge(b: Body) { return (this.tick - b.animAt) * DT; }
  private pushHist(b: Body) {
    const n = Math.min(b.histN, HIST - 1);
    b.hist.copyWithin(2, 0, n * 2); b.hist[0] = b.x; b.hist[1] = b.z; b.histN = n + 1;
  }
  allocId(): number {
    for (;;) { const id = 1 + Math.floor(this.rng() * 65534); if (!this.usedIds.has(id)) { this.usedIds.add(id); return id; } }
  }
  private newBody(kind: Kind, seat: number, costume: number): Body {
    return {
      uid: this.uidSeq++, id: this.allocId(), kind, seat, costume, x: 0, z: 0, h: 0, vx: 0, vz: 0,
      anim: "idle", animAt: this.tick, mode: "free", modeAt: 0, modeUntil: 0, anchored: false,
      blunderUntil: 0, glintUntil: 0, panicUntil: 0, revealed: false, removeAt: 0, visible: true, face: null, brain: null,
      hist: new Float32Array(HIST * 2), histN: 0, callAnim: null, freezeCall: false,
    };
  }
  /** a Sneak's costume: a resident type with ≥2 townsfolk lookalikes on the square right now (never the one just worn) */
  private sneakCostume(prev: number, avoid: Set<number>): number {
    const count = new Array(RESIDENTS.length).fill(0);
    for (const b of this.bodies) if (b.kind === "npc" && b.visible) count[b.costume]++;
    let ok = count.map((n, i) => (n >= 2 && i !== prev && !avoid.has(i) ? i : -1)).filter((i) => i >= 0);
    if (!ok.length) ok = count.map((n, i) => (n >= 2 && i !== prev ? i : -1)).filter((i) => i >= 0);
    if (!ok.length) ok = count.map((n, i) => (n >= 1 ? i : -1)).filter((i) => i >= 0);
    return ok.length ? pick(this.rng, ok) : 0;
  }
  /** round-start positions: random walkable points, spread out, away from the bandstand front and the bus door */
  private spawnSpots(n: number): M.V2[] {
    const out: M.V2[] = [], r = this.rng;
    for (let tries = 0; out.length < n && tries < 5000; tries++) {
      const x = range(r, -16.5, 16.5), z = range(r, -15.5, 16);
      if (M.blockedAt(x, z)) continue;
      if (Math.hypot(x - M.BANDSTAND.x, z - (M.BANDSTAND.z + M.BANDSTAND.r + 0.9)) < 3) continue;
      if (out.some((p) => Math.hypot(p.x - x, p.z - z) < 1.2)) continue;
      out.push({ x, z });
    }
    while (out.length < n) out.push(M.nearestFree(range(r, -10, 10), range(r, -10, 10)));
    return out;
  }

  // ===================================================================================================================
  // What may leave the sim. Everything a client, a bot or the wire learns is built HERE and nowhere else.

  /** public: the same for everyone. Sneaks and townsfolk are indistinguishable by construction: the same fields, the
   *  same costume indices, sorted by random id. Only blunder / glint flags (and a caught Sneak's reveal) mark a body. */
  publicView(): PublicView {
    const now = this.time;
    const bodies: PublicBody[] = [];
    for (const b of this.bodies) {
      if (!b.visible) continue;
      bodies.push({
        id: b.id, x: b.x, z: b.z, h: b.h, anim: b.anim, age: (this.tick - b.animAt) * DT, costume: b.costume,
        blunder: b.blunderUntil > now, glint: b.glintUntil > now, revealed: b.revealed, inspector: b.kind === "inspector",
      });
    }
    bodies.sort((a, b) => a.id - b.id);
    const insp = this.seats[this.inspectorSeat];
    return {
      tick: this.tick, time: now, phase: this.phase, round: this.round,
      phaseLeft: this.phase === "intro" ? this.playAt - now : this.phase === "play" ? T.ROUND_SEC - (now - this.playAt) : T.RESULTS_SEC - (now - this.phaseAt),
      playT: this.phase === "play" ? now - this.playAt : 0,
      vault: this.vault, goal: T.VAULT_GOAL, parade: this.parade, onboarding: this.onboarding, winner: this.winner,
      call: this.call ? { kind: this.call.kind, age: now - this.call.at } : null,
      whistle: this.whistle ? { kind: this.whistle.kind, age: now - this.whistle.at, by: this.whistle.by } : null,
      stalls: [...this.stalls], golden: this.golden, float: this.float ? { ...this.float } : null,
      panics: this.panics.map((p) => ({ x: p.x, z: p.z, left: p.until - now })),
      bus: { state: this.bus.state, age: now - this.bus.at },
      inspector: insp?.body?.id ?? 0, inspectorSeat: this.inspectorSeat, charges: insp?.charges ?? 0,
      inspectorCos: { hat: insp?.cos.hat ?? 0, ray: insp?.cos.ray ?? 0 },
      reveals: this.bodies.filter((b) => b.revealed && b.visible).map((b) => ({ id: b.id, cos: { ...this.seats[b.seat].cos } })),
      seats: this.seats.map((s) => ({ name: s.name, human: s.human, role: s.role })),
      nextInspectorSeat: this.phase === "results" ? this.nextInspectorSeat : -1,
      bodies,
    };
  }

  /** private: what ONE seat may know about itself (never about anyone else) */
  privateView(i: number): PrivateView {
    const s = this.seats[i], b = s.body, now = this.time;
    const locked = !b || b.mode !== "free" || this.phase !== "play";
    let can: PrivateView["can"] = "";
    if (b && s.role === "sneak" && this.phase === "play" && b.mode === "free") {
      if (this.call && s.callId === this.call.id && !s.callDone && now - this.call.at <= T.CALL_WINDOW) can = this.call.kind === "freeze" ? "freeze" : "answer";
      else if (s.carry === 0 && ((M.stallNear(b.x, b.z, T.GRAB_RANGE) >= 0 && this.stalls[M.stallNear(b.x, b.z, T.GRAB_RANGE)]) || (this.golden === 1 && this.float && Math.hypot(b.x - this.float.x, b.z - this.float.z) <= T.GOLDEN_GRAB_RANGE))) can = "grab";
      else if (s.carry > 0 && M.drainNear(b.x, b.z, T.STASH_RANGE) >= 0) can = "stash";
    }
    return {
      seat: i, role: s.role, me: b && b.visible && b.mode !== "popped" ? b.id : 0,
      x: b ? b.x : 0, z: b ? b.z : 0, h: b ? b.h : 0, locked, carry: s.carry,
      grab: s.grabT / T.GRAB_SEC, stash: s.stashT / T.STASH_SEC, can,
      respawnIn: s.respawnAt >= 0 ? Math.max(0, s.respawnAt - now) : -1,
      answered: this.call && s.callId === this.call.id ? (s.callDone ? (s.answerAt >= 0 ? (s.answerPerfect ? 2 : 1) : 3) : 0) : 0,
      freezeHold: !!b && b.mode === "call" && b.freezeCall,
      charges: s.charges, cooldown: Math.max(0, s.cooldownUntil - now), whistleIn: Math.max(0, s.whistleReadyAt - now), regenIn: s.regenAt >= 0 ? s.regenAt - now : -1, mark: s.mark,
      stashes: s.stats.stashes.length, perfects: s.stats.perfects, catches: s.stats.catches,
      nextInspector: this.phase === "results" && this.nextInspectorSeat === i,
      scriptGoal: s.role === "inspector" && !s.human ? this.scriptGoal : null,
      gentle: s.role === "inspector" && this.onboarding,
    };
  }
}

// =====================================================================================================================
// Views (the only shapes that leave the sim)

export type PublicBody = { id: number; x: number; z: number; h: number; anim: Anim; age: number; costume: number; blunder: boolean; glint: boolean; revealed: boolean; inspector: boolean };
export type PublicView = {
  tick: number; time: number; phase: Phase; round: number; phaseLeft: number; playT: number;
  vault: number; goal: number; parade: boolean; onboarding: boolean; winner: Role | "";
  call: { kind: Call; age: number } | null;
  whistle: { kind: Call; age: number; by: number } | null;
  stalls: boolean[]; golden: 0 | 1 | 2 | 3; float: { x: number; z: number; rot: number } | null;
  panics: { x: number; z: number; left: number }[];
  bus: { state: BusState; age: number };
  inspector: number; inspectorSeat: number; charges: number;
  inspectorCos: { hat: number; ray: number };
  reveals: { id: number; cos: Cosmetics }[];
  seats: { name: string; human: boolean; role: Role }[];
  nextInspectorSeat: number;
  bodies: PublicBody[];
};
export type PrivateView = {
  seat: number; role: Role; me: number; x: number; z: number; h: number; locked: boolean; carry: 0 | 1 | 2;
  grab: number; stash: number; can: "" | "grab" | "stash" | "answer" | "freeze";
  respawnIn: number; answered: 0 | 1 | 2 | 3; freezeHold: boolean;
  charges: number; cooldown: number; whistleIn: number; regenIn: number; mark: number;
  stashes: number; perfects: number; catches: number; nextInspector: boolean;
  scriptGoal: M.V2 | null; gentle: boolean;
};

// =====================================================================================================================
// Pure helpers (also used by the client's own-body prediction — keep them free of sim state)

const clamp1 = (n: number) => (Number.isFinite(n) ? Math.max(-1, Math.min(1, n)) : 0);
const finite3 = (v: Vec3) => Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
const r2 = (n: number) => Math.round(n * 100) / 100;
const ANIM_IS_CALL: Partial<Record<Anim, true>> = { dance: true, sit_ground: true, wave: true, jump: true, clap: true, freeze: true };
function callable(b: Body) { return b.visible && (b.mode === "free" || b.mode === "panic" || b.mode === "call"); }
function fixedBody(b: Body) { return b.anchored || b.mode === "popped" || b.mode === "tumble" || b.mode === "call" || (!!b.brain && b.brain.task === "act" && b.brain.act !== "bus"); }

/** stick → velocity: full speed past the dead zone (townsfolk only ever walk at one pace, so must you) */
export function moveVelocity(mx: number, mz: number, speed: number): { x: number; z: number } {
  const l = Math.hypot(mx, mz);
  if (l <= T.MOVE_DEADZONE) return { x: 0, z: 0 };
  return { x: (mx / l) * speed, z: (mz / l) * speed };
}
export function slewAngle(h: number, want: number, max: number): number {
  let d = want - h; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
  const n = h + Math.max(-max, Math.min(max, d));
  return n > Math.PI ? n - 2 * Math.PI : n < -Math.PI ? n + 2 * Math.PI : n;
}
/** one step of a player-controlled body ignoring other bodies: the client predicts its own body with exactly this */
export function predictStep(x: number, z: number, h: number, mx: number, mz: number, speed: number): { x: number; z: number; h: number } {
  const v = moveVelocity(mx, mz, speed);
  const p = M.collideStatic(x + v.x * DT, z + v.z * DT, T.BODY_R);
  const sp = Math.hypot(v.x, v.z);
  return { x: p.x, z: p.z, h: sp > 0.05 ? slewAngle(h, Math.atan2(v.x, v.z), T.TURN_RATE * DT) : h };
}
